// =====================================
// VIDEO MODAL COMPONENT
// Location: src/components/shared/VideoModal.tsx
// =====================================

import { X } from "lucide-react";
import { parseVideoUrl } from "@/lib/videoHelpers";
import { useEffect } from "react";

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title: string;
  description?: string;
}

export default function VideoModal({
  isOpen,
  onClose,
  videoUrl,
  title,
  description,
}: VideoModalProps) {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const parsedVideo = parseVideoUrl(videoUrl);

  if (!parsedVideo) {
    console.error("Invalid video URL:", videoUrl);
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal Content */}
      <div
        className="relative w-full max-w-5xl bg-white border-2 border-black"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 sm:-right-12 p-2 bg-white border-2 border-black hover:bg-gray-100 transition-colors z-10"
          aria-label="Close modal"
        >
          <X className="w-6 h-6" strokeWidth={3} />
        </button>

        {/* Video Container */}
        <div
          className="relative w-full bg-black"
          style={{ paddingBottom: "56.25%" }}
        >
          {parsedVideo.type === "youtube" && (
            <iframe
              src={parsedVideo.embedUrl}
              title={title}
              className="absolute top-0 left-0 w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}

          {parsedVideo.type === "vimeo" && (
            <iframe
              src={parsedVideo.embedUrl}
              title={title}
              className="absolute top-0 left-0 w-full h-full border-0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          )}

          {parsedVideo.type === "direct" && (
            <video
              src={parsedVideo.embedUrl}
              controls
              autoPlay
              className="absolute top-0 left-0 w-full h-full"
              style={{ objectFit: "contain" }}
            >
              Your browser does not support the video tag.
            </video>
          )}
        </div>

        {/* Title and Description */}
        <div className="p-4 sm:p-6 border-t-2 border-black">
          <h2 className="text-lg sm:text-xl font-bold text-black mb-2">
            {title}
          </h2>
          {description && (
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
